"use client";

import { useEffect, useRef } from "react";
import MarkdownRenderer from "./MarkdownRenderer";
import type { DeepResearchSource } from "@/app/lib/types";

// INVARIANT: never hand /corpus/documents/{key} to the browser; it needs an
// authenticated licensed seat. Only the Studio URL may open externally.
function studioUrlForCorpusSource(source: DeepResearchSource): string | undefined {
  return source.studio_open_url ?? source.studio_url;
}

interface DeepResearchDocumentProps {
  report: string;
  sources: DeepResearchSource[];
  brief: string;
  fromFallback?: boolean;
  onClose: () => void;
  onOpenCorpusDocument: (source: DeepResearchSource) => void;
}

export default function DeepResearchDocument({ report, sources, brief, fromFallback, onClose, onOpenCorpusDocument }: DeepResearchDocumentProps) {
  const corpusSources = sources.filter((source) => source.origin === "corpus");
  const webSources = sources.filter((source) => source.origin !== "corpus");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const corpusSourceForHref = (href: string) => {
    const pathname = new URL(href, window.location.href).pathname;
    return corpusSources.find((source) => new URL(source.url, window.location.href).pathname === pathname);
  };
  const handleCorpusLink = (event: React.MouseEvent<HTMLElement>) => {
    const anchor = (event.target as HTMLElement).closest("a");
    const href = anchor?.getAttribute("href");
    if (!href) return;
    const source = corpusSourceForHref(href);
    if (!source) return;
    event.preventDefault();
    const studioUrl = studioUrlForCorpusSource(source);
    if (studioUrl) {
      window.open(studioUrl, "_blank", "noopener,noreferrer");
      return;
    }
    onOpenCorpusDocument(source);
  };

  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const root = document.documentElement;
    const previous = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      rootOverflow: root.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    root.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      body.style.overflow = previous.bodyOverflow;
      root.style.overflow = previous.rootOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Deep Research report"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0, 0, 0, 0.96)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <article
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "75vw",
          height: "90vh",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)",
          padding: "10px 24px 20px 24px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header style={{ display: "flex", flexShrink: 0, justifyContent: "space-between", alignItems: "center", gap: "16px", marginBottom: "16px", paddingBottom: "16px", borderBottom: "1px solid var(--border)" }}>
          <h1 style={{ margin: 0, fontSize: "24px" }}>Deep Research report</h1>
          <button ref={closeButtonRef} className="btn-ghost" onClick={onClose} aria-label="Close document">
            Close
          </button>
        </header>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", padding: "0 clamp(0px, 2vw, 48px) 44px" }}>
          <section aria-label="Research brief" style={{ marginBottom: "32px" }}>
            <h2 style={{ fontSize: "16px", margin: "0 0 8px" }}>{fromFallback ? "Research question" : "Research brief"}</h2>
            <div style={{ whiteSpace: "pre-wrap", color: "var(--text-secondary)" }}>{brief}</div>
          </section>

          <div onClickCapture={handleCorpusLink}><MarkdownRenderer content={report} isStreamComplete /></div>

          {sources.length > 0 && (
            <section aria-label="Sources" style={{ marginTop: "40px", paddingTop: "20px", borderTop: "1px solid var(--border)" }}>
              <h2 style={{ fontSize: "16px", margin: "0 0 8px" }}>Sources</h2>
              {corpusSources.length > 0 && <section><h3 style={{ fontSize: "14px", margin: "10px 0 6px", color: "#cc6144" }}>Library</h3><ol style={{ listStyleType: "none", margin: 0, paddingLeft: "20px" }}>{corpusSources.map((source, index) => { const studioUrl = studioUrlForCorpusSource(source); return <li key={`${source.url}-${index}`} style={{ color: "#cc6144" }}><a href={studioUrl || source.url} {...(studioUrl ? { target: "_blank", rel: "noopener noreferrer" } : {})} onClick={(event) => { if (studioUrl) return; event.preventDefault(); onOpenCorpusDocument(source); }}>{source.label ?? "C" + (index + 1)}. {source.title || source.url}</a></li>; })}</ol></section>}
              {webSources.length > 0 && <section><h3 style={{ fontSize: "14px", margin: "10px 0 6px" }}>Web</h3><ol style={{ listStyleType: "none", margin: 0, paddingLeft: "20px" }}>{webSources.map((source, index) => <li key={`${source.url}-${index}`}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.label ?? index + 1}. {source.title || source.url}{source.publication_label && <span style={{ color: "var(--text-secondary)" }}> — {source.publication_label}</span>}</a></li>)}</ol></section>}
            </section>
          )}
        </div>
      </article>
    </div>
  );
}
